<script setup lang="ts">
// WTA-style public top nav (doc 04 §9.1): a floating white pill bar on the green
// page wash, deep-purple logo + pill nav links, a Players mega-menu, bell/search,
// and an outlined CTA.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useUiStore } from '@/stores/ui.store'

const ui = useUiStore()

function soon(label: string) {
  ui.toast(`${label} is coming soon.`, 'info')
}

// The LIVE badge and the Home pill alternate every 5s — a looping crossfade.
const showLive = ref(true)
let liveTimer: ReturnType<typeof setInterval> | undefined

// Auto-hide the floating nav on scroll-down, reveal it on scroll-up (and always
// show it at the very top of the page).
const hidden = ref(false)
let lastY = 0
function onScroll() {
  const y = window.scrollY
  if (y < 16) hidden.value = false
  else if (y > lastY + 4 && y > 120) hidden.value = true
  else if (y < lastY - 4) hidden.value = false
  lastY = y
}

onMounted(() => {
  liveTimer = setInterval(() => {
    showLive.value = !showLive.value
  }, 5000)
  lastY = window.scrollY
  window.addEventListener('scroll', onScroll, { passive: true })
})
onBeforeUnmount(() => {
  if (liveTimer) clearInterval(liveTimer)
  window.removeEventListener('scroll', onScroll)
})

interface NavLink {
  title: string
  to: string
}

const links: NavLink[] = [
  { title: 'Home', to: '/' },
  { title: 'Rankings', to: '/rankings' },
  { title: 'Players', to: '/players' },
  { title: 'Tournaments', to: '/tournaments' },
  { title: 'Courts', to: '/courts' },
  { title: 'Clubs', to: '/clubs' },
  { title: 'News', to: '/news' },
]

const drawer = ref(false)
</script>

<template>
  <header class="cpa-nav" :class="{ 'cpa-nav--hidden': hidden }">
    <div class="cpa-nav__bar">
      <RouterLink to="/" class="cpa-nav__logo cpa-display">CPA</RouterLink>

      <!-- LIVE badge and Home pill alternate on a looping 5s crossfade. -->
      <div class="cpa-nav__rotator">
        <Transition name="cpa-fade" mode="out-in">
          <button
            v-if="showLive"
            key="live"
            type="button"
            class="cpa-nav__live"
            @click="soon('Live scoring')"
          >
            <span class="cpa-nav__live-dot"></span>
            Live
          </button>
          <RouterLink v-else key="home" to="/" class="cpa-nav__homepill">
            Home
          </RouterLink>
        </Transition>
      </div>

      <nav class="cpa-nav__links d-none d-md-flex">
        <RouterLink
          to="/rankings"
          class="cpa-nav__link"
          active-class="cpa-nav__link--active"
        >
          Rankings
        </RouterLink>

        <!-- Players mega-menu -->
        <v-menu open-on-hover location="bottom start" :offset="12">
          <template #activator="{ props: menuProps }">
            <button type="button" class="cpa-nav__link cpa-nav__link--btn" v-bind="menuProps">
              Players
              <v-icon icon="mdi-chevron-down" size="18" />
            </button>
          </template>
          <v-card class="cpa-megamenu" rounded="xl">
            <div class="cpa-megamenu__grid">
              <div>
                <div class="cpa-eyebrow text-medium-emphasis mb-2">Explore</div>
                <RouterLink to="/players" class="cpa-megamenu__link cpa-display">
                  Player Listing
                </RouterLink>
                <RouterLink to="/rankings" class="cpa-megamenu__link cpa-display">
                  Rankings
                </RouterLink>
                <RouterLink to="/clubs" class="cpa-megamenu__link cpa-display">
                  Clubs
                </RouterLink>
                <RouterLink to="/courts" class="cpa-megamenu__link cpa-display">
                  Courts &amp; Booking
                </RouterLink>
                <RouterLink to="/news" class="cpa-megamenu__link cpa-display">
                  News
                </RouterLink>
              </div>
              <RouterLink to="/rankings" class="cpa-megamenu__feature">
                <div class="cpa-eyebrow mb-1">Spotlight</div>
                <div class="cpa-display text-h6 mb-1">Top of the Caraga table</div>
                <div class="text-caption">See who leads Region XIII →</div>
              </RouterLink>
            </div>
          </v-card>
        </v-menu>

        <RouterLink
          to="/tournaments"
          class="cpa-nav__link"
          active-class="cpa-nav__link--active"
        >
          Tournaments
        </RouterLink>
        <RouterLink
          to="/courts"
          class="cpa-nav__link"
          active-class="cpa-nav__link--active"
        >
          Courts
        </RouterLink>
        <RouterLink
          to="/clubs"
          class="cpa-nav__link"
          active-class="cpa-nav__link--active"
        >
          Clubs
        </RouterLink>
        <RouterLink
          to="/news"
          class="cpa-nav__link"
          active-class="cpa-nav__link--active"
        >
          News
        </RouterLink>

        <!-- About mega-menu -->
        <v-menu open-on-hover location="bottom start" :offset="12">
          <template #activator="{ props: menuProps }">
            <button type="button" class="cpa-nav__link cpa-nav__link--btn" v-bind="menuProps">
              About
              <v-icon icon="mdi-chevron-down" size="18" />
            </button>
          </template>
          <v-card class="cpa-megamenu" rounded="xl">
            <div class="cpa-megamenu__grid">
              <div>
                <div class="cpa-eyebrow text-medium-emphasis mb-2">The Association</div>
                <a class="cpa-megamenu__link cpa-display" @click.prevent="soon('About CPA')">About CPA</a>
                <a class="cpa-megamenu__link cpa-display" @click.prevent="soon('Our History')">Our History</a>
                <a class="cpa-megamenu__link cpa-display" @click.prevent="soon('Press Center')">Press Center</a>
                <a class="cpa-megamenu__link cpa-display" @click.prevent="soon('Officials & Referees')">Officials</a>
              </div>
              <RouterLink to="/register" class="cpa-megamenu__feature">
                <div class="cpa-eyebrow mb-1">Get involved</div>
                <div class="cpa-display text-h6 mb-1">Join the CPA</div>
                <div class="text-caption">Become a member of the Caraga league →</div>
              </RouterLink>
            </div>
          </v-card>
        </v-menu>
      </nav>

      <div class="cpa-nav__actions">
        <v-btn
          icon
          variant="text"
          color="primary"
          size="small"
          class="d-none d-sm-inline-flex"
        >
          <v-badge color="success" dot>
            <v-icon icon="mdi-bell-outline" />
          </v-badge>
        </v-btn>
        <v-btn
          icon="mdi-magnify"
          variant="text"
          color="primary"
          size="small"
          class="d-none d-sm-inline-flex"
        />
        <v-btn
          to="/login"
          variant="outlined"
          color="primary"
          class="cpa-nav__cta"
          prepend-icon="mdi-account-outline"
        >
          Sign in
        </v-btn>
        <v-app-bar-nav-icon
          class="d-md-none"
          color="primary"
          @click="drawer = !drawer"
        />
      </div>
    </div>
  </header>

  <v-navigation-drawer v-model="drawer" temporary location="left">
    <v-list nav>
      <v-list-item
        v-for="link in links"
        :key="link.to"
        :to="link.to"
        :title="link.title"
      />
      <v-divider class="my-2" />
      <v-list-item
        to="/login"
        title="Sign in"
        prepend-icon="mdi-account-outline"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<style scoped>
.cpa-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 16px 16px 4px;
  transition: transform 0.34s var(--cpa-ease);
  will-change: transform;
}
/* Slide the whole floating bar (and its padding) out of view on scroll-down. */
.cpa-nav--hidden {
  transform: translateY(-130%);
}
@media (prefers-reduced-motion: reduce) {
  .cpa-nav {
    transition: none;
  }
}
.cpa-nav__bar {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 999px;
  padding: 8px 12px 8px 24px;
  box-shadow: 0 12px 34px rgba(20, 5, 40, 0.14);
}
.cpa-nav__logo {
  font-size: 1.75rem;
  font-style: italic;
  font-weight: 800;
  color: var(--cpa-purple);
  text-decoration: none;
  letter-spacing: -0.02em;
  line-height: 1;
}
.cpa-nav__live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border: none;
  border-radius: 999px;
  background: #d6f84a;
  color: var(--cpa-purple);
  font-family: inherit;
  font-weight: 800;
  font-size: 0.6rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
}
.cpa-nav__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e4482e;
  animation: cpa-live-pulse 1.2s ease-in-out infinite;
}
/* The slot that crossfades between the LIVE badge and the Home pill. */
.cpa-nav__rotator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  margin-left: 10px;
}
.cpa-nav__homepill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 999px;
  background: var(--cpa-purple);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  text-decoration: none;
  white-space: nowrap;
}
/* Scroll-style swap: the leaving pill fades down and out, the entering one
   fades in from above (so Live drops to the bottom, Home slides in on top). */
.cpa-fade-enter-active,
.cpa-fade-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}
.cpa-fade-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}
.cpa-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
@keyframes cpa-live-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.8);
  }
}
.cpa-nav__links {
  align-items: center;
  gap: 10px;
  margin-left: 22px;
}
.cpa-nav__link {
  text-decoration: none;
  color: var(--cpa-purple);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 9px 18px;
  border-radius: 999px;
  transition: background 0.15s ease;
}
.cpa-nav__link:hover {
  background: rgba(43, 10, 64, 0.07);
}
.cpa-nav__link--active {
  background: var(--cpa-purple);
  color: #fff;
}
.cpa-nav__link--btn {
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: inherit;
}
.cpa-nav__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
}
.cpa-nav__cta {
  font-weight: 700;
  margin-left: 4px;
}

/* Mega-menu (teleported, but carries this component's scope attribute) */
.cpa-megamenu {
  padding: 22px;
  min-width: 440px;
  box-shadow: 0 18px 50px rgba(20, 5, 40, 0.2);
}
.cpa-megamenu__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
}
.cpa-megamenu__link {
  display: block;
  text-decoration: none;
  color: var(--cpa-purple);
  font-size: 1.1rem;
  padding: 7px 0;
  cursor: pointer;
  transition: color 0.15s ease;
}
.cpa-megamenu__link:hover {
  color: var(--cpa-green);
}
.cpa-megamenu__link.is-soon {
  color: rgba(43, 10, 64, 0.32);
  cursor: default;
}
.cpa-megamenu__feature {
  display: block;
  text-decoration: none;
  background: linear-gradient(135deg, var(--cpa-purple) 0%, var(--cpa-purple-2) 100%);
  color: #fff;
  border-radius: 16px;
  padding: 18px;
}
.cpa-megamenu__feature .cpa-eyebrow {
  color: var(--cpa-green-bright);
}
</style>
